/* ==========================================================================
   Super Effective — cart drawer, volume goal engine, quantity steppers.

   Design notes
   ------------
   * The drawer never templates HTML in JS. Every mutation asks Shopify to
     re-render `sections/se-cart-drawer.liquid` through the Cart AJAX API's
     `sections` parameter, so money formatting, discounts, inventory caps and
     the goal maths always come from Liquid.
   * `#SECartDrawerInner` is the only node swapped. The footer lives outside it
     because `content_for_additional_checkout_buttons` (Shop Pay / Apple Pay)
     cannot survive an innerHTML replacement.
   * Every cart request goes through one serialised queue, so a burst of
     stepper clicks can never interleave and land the cart in a stale state.
   ========================================================================== */

(function () {
  'use strict';

  const SECTION_ID = 'se-cart-drawer';
  const REWARD_PROPERTY = '_se_reward';
  const MAX_REWARD_PASSES = 4;
  const QTY_DEBOUNCE_MS = 400;
  const CLOSE_SELECTOR = '[data-se-close]:not(.se-drawer__overlay)';
  const BLOCKED_REWARDS_KEY = 'se-cart-blocked-rewards';

  /** Rewards that /cart/add refuses (sold out, etc). Kept for the whole session
      so an unaddable reward does not fire a failing POST on every page view. */
  const readBlockedRewards = () => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(BLOCKED_REWARDS_KEY) || '[]'));
    } catch (error) {
      return new Set();
    }
  };

  const writeBlockedRewards = (set) => {
    try {
      sessionStorage.setItem(BLOCKED_REWARDS_KEY, JSON.stringify([...set]));
    } catch (error) {
      // Private mode / storage disabled — in-memory only is fine.
    }
  };

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartRoutes = () => window.routes || {};

  // Two spellings of the same endpoint, and the difference is analytics.
  //
  // `window.routes.cart_add_url` is the FORM endpoint, `/cart/add`. It works —
  // with Accept: application/json it even answers in JSON — but Shopify's Web
  // Pixels Manager instruments the AJAX Cart API, the `.js` spelling, and that
  // interception is what emits `product_added_to_cart`. Post to the form
  // endpoint and the cart updates while the pixel never hears about it, so
  // Meta's AddToCart and everything downstream of it goes unreported.
  //
  // So: anything the SHOPPER did goes through loud(), and the reward engine's
  // own bookkeeping goes through silent(). Rewards are added BY this script,
  // not chosen by the shopper — routing those through the instrumented
  // endpoint would report an AddToCart nobody performed and inflate the very
  // number we are trying to make trustworthy.
  const loud = (url, fallback) => {
    const base = String(url || fallback).replace(/\/+$/, '');
    return /\.js$/.test(base) ? base : base + '.js';
  };
  const silent = (url, fallback) => String(url || fallback).replace(/\.js$/, '');

  const addUrl = () => loud(cartRoutes().cart_add_url, '/cart/add');
  const changeUrl = () => loud(cartRoutes().cart_change_url, '/cart/change');
  const updateUrl = () => loud(cartRoutes().cart_update_url, '/cart/update');
  const silentAddUrl = () => silent(cartRoutes().cart_add_url, '/cart/add');
  const silentChangeUrl = () => silent(cartRoutes().cart_change_url, '/cart/change');
  const silentUpdateUrl = () => silent(cartRoutes().cart_update_url, '/cart/update');
  // Declared up here on purpose: <se-pack-builder> upgrades synchronously inside
  // customElements.define(), and its first render() reads the cart through this.
  // Left further down it sat in the temporal dead zone and that render threw.
  const getDrawer = () => document.querySelector('se-cart-drawer');
  // The page the shelf is rendered for, query included: free packs depend on
  // the template, and a ?view=v1a preview is a different template from the
  // bare path. Every section render goes through this.
  const pageUrl = () => window.location.pathname + window.location.search;

  /* ------------------------------------------------------------------ money */

  /** Money strings come from Liquid's `money` filter, which can contain markup
      or entities (&pound;, <span class=money>). textContent would print those
      literally, so everything money-shaped is written through innerHTML. */
  /** Money strings may carry markup; announcements want the plain text. */
  const stripTags = (html) => String(html == null ? '' : html).replace(/<[^>]*>/g, '').trim();

  const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ESCAPES[c]);

  /** Marks a string as already-safe markup for `fill()`. Everything not wrapped
      in this is merchant- or shopper-derived text and gets escaped. */
  const markup = (value) => ({ html: String(value) });

  const setMoney = (element, html) => {
    if (element) element.innerHTML = html;
  };

  const SEMoney = {
    format(cents) {
      const value = Number(cents) || 0;

      // Prefer the visitor's presentment currency — `shop.money_format` is
      // pinned to the shop currency and is wrong under Shopify Markets.
      const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (active) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: active,
          }).format(value / 100);
        } catch (error) {
          // Fall through to the money_format parser.
        }
      }

      const format = window.SEMoneyFormat || '${{amount}}';

      const withDelimiters = (number, precision, thousands, decimal) => {
        const fixed = (number / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return parts[1] ? whole + decimal + parts[1] : whole;
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
        switch (token) {
          case 'amount_no_decimals':
            return withDelimiters(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return withDelimiters(value, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return withDelimiters(value, 0, ' ', ',');
          case 'amount_with_space_separator':
            return withDelimiters(value, 2, ' ', ',');
          default:
            return withDelimiters(value, 2, ',', '.');
        }
      });
    },
  };

  window.SEMoney = SEMoney;

  /* ------------------------------------------------------------- cart drawer */

  class SECartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.inner = this.querySelector('#SECartDrawerInner');
      this.panel = this.querySelector('.se-drawer__panel');
      this.footer = this.querySelector('#SECartFooterForm');
      this.queue = Promise.resolve();
      this.qtyTimers = new Map();
      this.blockedRewardVariants = readBlockedRewards();
      // Per reward variant: the most Shopify gave free at a given spend, learnt
      // from a split line. See reconcileRewards.
      this.rewardCeilings = new Map();
      this.state = null;
      this.openedBy = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      this.syncFromState(true);

      // Arrived from /cart (checkout's bag icon) via the sticky-variant
      // redirect: show the shelf, then drop the flag from the address bar.
      const params = new URLSearchParams(window.location.search);
      if (params.get('se_cart') === 'open') {
        params.delete('se_cart');
        const query = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
        this.open();
      }
      // A returning shopper may already be over a threshold. Reconcile, but do
      // not fire the toast/chime/buzz for it — nothing just happened, and a
      // celebration on page load with no user gesture is jarring (and the chime
      // would be blocked by the autoplay policy anyway).
      this.silentReconcile = true;
      this.enqueue(() => this.reconcileRewards()).finally(() => {
        this.silentReconcile = false;
      });
    }

    /* -- open / close -- */

    open(triggeredBy) {
      if (triggeredBy) this.openedBy = triggeredBy;
      this.classList.add('is-open');
      document.documentElement.classList.add('se-scroll-lock');
      this.setAttribute('aria-hidden', 'false');

      // Focus synchronously — deferring to requestAnimationFrame drops the
      // focus move whenever the window is not the foreground one.
      const target = this.querySelector(CLOSE_SELECTOR) || this.panel;
      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, target);
      } else if (target) {
        target.focus({ preventScroll: true });
      }
    }

    close() {
      // Cleared here, not in open(): addItems/addFromForm set an error and then
      // open() in the same tick, and clearing on open would erase it.
      this.showError(null);
      this.classList.remove('is-open');
      document.documentElement.classList.remove('se-scroll-lock');
      this.setAttribute('aria-hidden', 'true');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.openedBy);
      } else if (this.openedBy && typeof this.openedBy.focus === 'function') {
        this.openedBy.focus();
      }
      this.openedBy = null;
    }

    setActiveElement(element) {
      this.openedBy = element;
    }

    /* -- events -- */

    onClick(event) {
      const closer = event.target.closest('[data-se-close]');
      if (closer && this.contains(closer)) {
        event.preventDefault();
        this.close();
        return;
      }

      const step = event.target.closest('[data-se-qty-step]');
      if (step) {
        event.preventDefault();
        this.stepLine(step);
        return;
      }

      const remove = event.target.closest('[data-se-remove]');
      if (remove) {
        event.preventDefault();
        const line = remove.closest('[data-se-line]');
        // flushLine (not changeLine) so a stepper timer still pending for this
        // line is cancelled — otherwise it fires against a dead line key.
        if (line) this.flushLine(line.dataset.seKey, 0, 0);
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-se-qty-input]');
      if (!input) return;
      const line = input.closest('[data-se-line]');
      if (!line) return;
      const quantity = this.clampLineQuantity(input, parseInt(input.value, 10));
      input.value = quantity;
      this.flushLine(line.dataset.seKey, quantity, 0);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        event.stopPropagation();
        this.close();
      }
    }

    clampLineQuantity(input, value) {
      let quantity = Number.isFinite(value) ? value : 1;
      if (quantity < 0) quantity = 0;
      const max = parseInt(input.getAttribute('max'), 10);
      if (Number.isFinite(max) && max > 0 && quantity > max) quantity = max;
      return quantity;
    }

    stepLine(button) {
      const line = button.closest('[data-se-line]');
      const input = line && line.querySelector('[data-se-qty-input]');
      if (!line || !input) return;

      const delta = parseInt(button.dataset.seQtyStep, 10) || 0;
      const quantity = this.clampLineQuantity(input, (parseInt(input.value, 10) || 0) + delta);
      input.value = quantity;
      this.flushLine(line.dataset.seKey, quantity, QTY_DEBOUNCE_MS);
    }

    /** Every cart key behind a row — more than one when Shopify split the line. */
    keysFor(key) {
      const escaped = window.CSS && CSS.escape ? CSS.escape(key) : key;
      const line = this.querySelector(`[data-se-line][data-se-key="${escaped}"]`);
      const keys = line && line.dataset.seKeys ? line.dataset.seKeys.split(',').filter(Boolean) : [];
      return keys.length > 1 ? keys : [key];
    }

    /** Debounce per line so rapid stepper clicks collapse into one request. */
    flushLine(key, quantity, wait) {
      if (!key) return;
      const existing = this.qtyTimers.get(key);
      if (existing) clearTimeout(existing);
      // Read the keys now: by the time a debounced change fires, the row may
      // have been re-rendered.
      const keys = this.keysFor(key);

      if (!wait) {
        this.qtyTimers.delete(key);
        this.changeLine(key, quantity, keys);
        return;
      }

      this.qtyTimers.set(
        key,
        setTimeout(() => {
          this.qtyTimers.delete(key);
          this.changeLine(key, quantity, keys);
        }, wait)
      );
    }

    /* -- cart mutations -- */

    enqueue(task) {
      const next = this.queue.then(() => task());
      this.queue = next.catch(() => {});
      return next;
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
    }

    showError(message) {
      const node = this.querySelector('[data-se-error]');
      if (!node) return;
      if (!message) {
        node.hidden = true;
        node.textContent = '';
        return;
      }
      node.hidden = false;
      node.textContent = message;
    }

    async request(url, body, options) {
      const config = {
        method: 'POST',
        headers: { Accept: 'application/json' },
      };

      if (body instanceof FormData) {
        config.body = body;
      } else {
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(Object.assign({ sections: SECTION_ID, sections_url: pageUrl() }, body));
      }

      const response = await fetch(url, config);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(data.description || data.message || 'Cart update failed');
        error.data = data;
        throw error;
      }

      // Under ?view=<template> (previewing a page template before it is
      // assigned) Shopify renders the returned section for the page's OWN
      // template — it drops the query from sections_url — so the shelf would
      // come back with another variant's rewards. A plain GET keeps ?view.
      const viewPreview = /[?&]view=/.test(window.location.search);
      if (data.sections && data.sections[SECTION_ID] && !viewPreview) {
        this.applySection(data.sections[SECTION_ID]);
      } else if (!(options && options.skipRefresh)) {
        await this.refresh();
      }

      return data;
    }

    async refresh() {
      // Under ?view=<template> Shopify answers a sections request with the
      // page's own JSON, not the section — so fetch the page itself and lift
      // the shelf out of it (applySection only needs #SECartDrawerInner).
      if (/[?&]view=/.test(window.location.search)) {
        const page = await fetch(pageUrl(), { cache: 'no-store' });
        if (!page.ok) throw new Error('Could not refresh the cart.');
        this.applySection(await page.text());
        return;
      }
      // Rendered for THIS page, not the homepage: whether free packs are on
      // depends on the template (see snippets/se-rewards-on.liquid), and every
      // other request already sends sections_url = the current path.
      const url = `${pageUrl()}${window.location.search ? '&' : '?'}sections=${SECTION_ID}`;
      // no-store: a cached copy is the cart as it was, which is exactly what a
      // refresh after a mutation must not show.
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Could not refresh the cart.');
      const data = await response.json().catch(() => ({}));
      if (!data || !data[SECTION_ID]) throw new Error('Could not refresh the cart.');
      this.applySection(data[SECTION_ID]);
    }

    applySection(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.getElementById('SECartDrawerInner');
      // Throw rather than return: a silent no-op leaves this.state stale, and
      // reconcileRewards would then re-add the same reward on every pass.
      if (!next) throw new Error('Could not render the cart.');
      if (!this.inner) return;

      // Any stepper timers belong to markup that is about to be destroyed.
      this.qtyTimers.forEach((timer) => clearTimeout(timer));
      this.qtyTimers.clear();

      const scrollTop = this.inner.scrollTop;
      const focused = this.describeFocus(document.activeElement);

      this.inner.innerHTML = next.innerHTML;
      this.inner.scrollTop = scrollTop;

      this.syncFromState();
      this.restoreFocus(focused);
    }

    /** Remember which control had focus, by line + role, across the swap. */
    describeFocus(element) {
      if (!element || !this.contains(element)) return null;

      const line = element.closest('[data-se-line]');
      if (line) {
        const key = line.dataset.seKey;
        if (element.matches('[data-se-qty-step]')) {
          return { key, selector: `[data-se-qty-step="${element.dataset.seQtyStep}"]` };
        }
        if (element.matches('[data-se-qty-input]')) return { key, selector: '[data-se-qty-input]' };
        if (element.matches('[data-se-remove]')) return { key, selector: '[data-se-remove]' };
      }
      if (element.matches('[data-se-close]')) return { selector: CLOSE_SELECTOR };
      return null;
    }

    /**
     * Put focus back and re-arm the trap. Dawn's trapFocus snapshots the first
     * and last focusable nodes, so after an innerHTML swap its boundaries point
     * at detached elements — Tab dies and Shift+Tab escapes the dialog.
     */
    restoreFocus(focused) {
      if (!this.classList.contains('is-open')) return;

      let target = null;

      // A control that disabled itself before the request has already blurred,
      // so describeFocus saw <body>. Honour an explicit hint instead.
      if (this.pendingFocus) {
        target = this.querySelector(this.pendingFocus);
        this.pendingFocus = null;
      }

      if (!target && focused) {
        if (focused.key) {
          const escaped = window.CSS && CSS.escape ? CSS.escape(focused.key) : focused.key;
          const line = this.querySelector(`[data-se-key="${escaped}"]`);
          target = line && line.querySelector(focused.selector);
        } else {
          target = this.querySelector(focused.selector);
        }
      }
      if (!target) target = this.querySelector(CLOSE_SELECTOR);
      if (!target) return;

      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, target);
      } else {
        target.focus({ preventScroll: true });
      }
    }

    /** Read the JSON blob Liquid emits and mirror it into the persistent chrome. */
    syncFromState(silent) {
      const node = this.querySelector('[data-se-cart-state]');
      if (!node) return;

      try {
        this.state = JSON.parse(node.textContent);
      } catch (error) {
        this.state = null;
        return;
      }

      const count = this.state.item_count || 0;
      this.classList.toggle('is-empty', count === 0);

      document.querySelectorAll('[data-se-cart-count]').forEach((element) => {
        element.textContent = count > 99 ? '99+' : String(count);
        element.hidden = count === 0;
      });

      // The bubble is aria-hidden, so screen readers read this instead.
      document.querySelectorAll('[data-se-cart-count-text]').forEach((element) => {
        element.textContent = count === 1 ? '1 item' : `${count} items`;
      });

      setMoney(this.querySelector('[data-se-subtotal]'), this.state.subtotal);

      const savings = this.querySelector('[data-se-savings]');
      if (savings) {
        savings.hidden = !(this.state.savings > 0);
        setMoney(savings.querySelector('[data-se-savings-value]'), this.state.savings_formatted);
      }

      if (!silent) this.announce();

      const checkout = this.querySelector('[data-se-checkout]');
      if (checkout) checkout.disabled = count === 0;

      if (!silent && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'se-cart-drawer', cartData: this.state });
      }

      // On-page components read the cart through getDrawer().state, which is
      // only correct the moment it is parsed. Announce it so the pack builder
      // can re-run: its reward line is measured against cart spend, and its
      // stock ceiling against what is already in the shelf, and both go stale
      // whenever the cart changes somewhere else on the page.
      document.dispatchEvent(new CustomEvent('se:cart:updated', { detail: { state: this.state } }));

      this.applyShippingCode();
      this.rememberPage();
    }

    /**
     * Remember which CRO page this shopper is on, in the hidden _se_page cart
     * attribute. Checkout sends them to the homepage (logo) or /cart (bag
     * icon); layout/theme.liquid reads this to send them back here instead.
     */
    rememberPage() {
      if (this.pageRemembered || !this.state) return;
      this.pageRemembered = true;
      const path = window.location.pathname;
      if (!/\/pages\//.test(path) || this.state.page_attr === path) return;
      this.enqueue(() =>
        fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { _se_page: path } }),
        }).catch(() => {})
      );
    }

    /**
     * Free shipping on the CRO pages: once the cart reaches the threshold, put
     * the free-shipping code on the cart so checkout applies it — no typing.
     * The code itself carries the $50 minimum and the rate cap in Shopify, so
     * this only decides WHEN to attach it. Codes the shopper already applied
     * are kept (the Cart API replaces the whole list). Below the threshold the
     * code is left alone: Shopify simply treats it as not applicable.
     */
    applyShippingCode() {
      const state = this.state;
      if (!state || !state.ship_code || !state.ship_threshold) return;
      if (state.total_price < state.ship_threshold) return;
      const code = String(state.ship_code).trim();
      if (!code || this.shippingCodeOn === code || this.shippingCodeBusy) return;

      this.shippingCodeBusy = true;
      this.enqueue(async () => {
        try {
          const cart = await (await fetch(`${root()}cart.js`, { cache: 'no-store' })).json();
          const current = (cart.discount_codes || []).map((entry) => entry.code).filter(Boolean);
          if (current.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
            this.shippingCodeOn = code;
            return;
          }
          const response = await fetch(`${root()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: current.concat(code).join(',') }),
          });
          if (response.ok) this.shippingCodeOn = code;
        } catch (error) {
          // Checkout still works without it; the next render tries again.
        } finally {
          this.shippingCodeBusy = false;
        }
      });
    }

    /**
     * The only announcement channel. The goal meter's aria-live node is inside
     * the swapped subtree, and a recreated live region is never announced.
     */
    announce() {
      const status = this.querySelector('[data-se-status]');
      if (!status || !this.state) return;

      const count = this.state.item_count || 0;
      const goal = this.querySelector('.se-goal__msg');
      const parts = [count === 1 ? '1 item in cart' : `${count} items in cart`];
      if (this.state.subtotal) parts.push(`subtotal ${stripTags(this.state.subtotal)}`);
      if (goal) parts.push(goal.textContent.replace(/\s+/g, ' ').trim());

      status.textContent = parts.join(', ');
    }

    /* -- public API -------------------------------------------------------- */

    /** Compatibility shim for Dawn's product-form.js / quick-add.js. */
    getSectionsToRender() {
      return [{ id: SECTION_ID, section: SECTION_ID, selector: '#SECartDrawerInner' }];
    }

    /** Called by Dawn's product-form.js with the /cart/add.js response. */
    renderContents(parsedState) {
      // See request(): under ?view= the returned section is for the wrong template.
      const viewPreview = /[?&]view=/.test(window.location.search);
      const html = !viewPreview && parsedState && parsedState.sections && parsedState.sections[SECTION_ID];
      this.enqueue(async () => {
        try {
          if (html) {
            this.applySection(html);
          } else {
            await this.refresh();
          }
          await this.reconcileRewards();
        } catch (error) {
          // The item is in the cart either way — never leave the shopper with a
          // stopped spinner and no drawer.
          await this.refresh().catch(() => {});
          this.showError(error.message);
        } finally {
          this.open(this.openedBy);
        }
      });
    }

    changeLine(key, quantity, keys) {
      return this.enqueue(async () => {
        this.setBusy(true);
        this.showError(null);
        try {
          if (keys && keys.length > 1) {
            // A row Shopify split into several lines: the shopper set the TOTAL.
            // Put it on the first key and zero the rest in one update; Shopify
            // re-splits if the discount still calls for it.
            const updates = {};
            keys.forEach((lineKey, index) => {
              updates[lineKey] = index === 0 ? quantity : 0;
            });
            await this.request(updateUrl(), { updates });
          } else {
            await this.request(changeUrl(), { id: key, quantity });
          }
          await this.reconcileRewards();
        } catch (error) {
          await this.refresh().catch(() => {});
          this.showError(error.message);
        } finally {
          this.setBusy(false);
        }
      });
    }

    /**
     * Add any number of lines in ONE request, then open the shelf. Used by the
     * pack builder and by the in-shelf quick-add, so both inherit the queue,
     * the reward reconcile, the error path and the focus handling.
     *
     * @param {Array<{id:Number, quantity:Number}>} items
     * @param {Element} [opener]  what to return focus to
     * @param {Boolean} [keepClosed]  quick-add inside an open shelf
     */
    addItems(items, opener, keepClosed) {
      const lines = (items || []).filter((i) => i && i.id && i.quantity > 0);
      if (!lines.length) return Promise.resolve();

      // Resolves true only when the cart actually changed. /cart/add.js is
      // atomic for an items array — one bad line adds nothing — so the caller
      // must be able to tell, or it will throw away a selection that never
      // reached the cart.
      return this.enqueue(async () => {
        let ok = false;
        this.showError(null);
        this.setBusy(true);
        try {
          await this.request(addUrl(), { items: lines });
          await this.reconcileRewards();
          ok = true;
        } catch (error) {
          await this.refresh().catch(() => {});
          this.showError(error.message);
        } finally {
          this.setBusy(false);
          if (!keepClosed) this.open(opener);
        }
        return ok;
      });
    }

    addFromForm(form) {
      const submit = form.querySelector('[data-se-add-cta]') || form.querySelector('[type="submit"]');

      return this.enqueue(async () => {
        this.showError(null);
        if (submit) {
          submit.disabled = true;
          submit.setAttribute('aria-busy', 'true');
        }

        try {
          const formData = new FormData(form);
          formData.append('sections', SECTION_ID);
          formData.append('sections_url', pageUrl());
          await this.request(addUrl(), formData);
          await this.reconcileRewards();
          this.open(submit);
        } catch (error) {
          await this.refresh().catch(() => {});
          this.showError(error.message);
          this.open(submit);
        } finally {
          if (submit) {
            submit.disabled = false;
            submit.removeAttribute('aria-busy');
          }
        }
      });
    }

    /* -- the "reward unlocked" moment ------------------------------------- */

    /**
     * Fires once, when a reward actually lands in the shelf. Deliberately quiet:
     * a short two-note chime synthesised in the browser, a 12ms buzz, and a
     * toast. No audio file, and nothing borrowed from any game — the client
     * asked about a Pokémon sound and that is not ours to ship.
     */
    celebrate(label) {
      if (this.silentReconcile) return;
      const cfg = window.SERewardFx || {};

      if (cfg.toast !== false) {
        const node = this.querySelector('[data-se-reward-toast]');
        if (node) {
          const template = cfg.toastText || '✨ Reward unlocked — [reward] added';
          node.textContent = template.replace('[reward]', label || 'your free pack');
          node.hidden = false;
          clearTimeout(this.toastTimer);
          // Re-trigger the entrance even if a toast is already showing.
          node.classList.remove('is-in');
          void node.offsetWidth;
          node.classList.add('is-in');
          this.toastTimer = setTimeout(() => {
            node.classList.remove('is-in');
            this.toastHideTimer = setTimeout(() => {
              node.hidden = true;
            }, 400);
          }, 4200);
        }
      }

      if (cfg.haptic !== false && navigator.vibrate) {
        // Android only; iOS Safari does not expose this.
        try {
          navigator.vibrate([12, 40, 18]);
        } catch (error) {
          /* ignore */
        }
      }

      this.chime(cfg);
    }

    chime(cfg) {
      const volume = Math.max(0, Math.min(30, cfg.volume == null ? 8 : cfg.volume)) / 100;
      if (cfg.sound === false || volume === 0) return;
      if (document.hidden) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;

      try {
        // Always follows a click, so the autoplay policy is satisfied.
        const ctx = (SECartDrawer.audio = SECartDrawer.audio || new Ctx());
        if (ctx.state === 'suspended') ctx.resume();

        const now = ctx.currentTime;
        const master = ctx.createGain();
        master.gain.value = volume;
        master.connect(ctx.destination);

        // A soft rising perfect fifth — E6 then B6.
        [[1318.51, 0], [1975.53, 0.09]].forEach(([freq, offset]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.0001, now + offset);
          gain.gain.exponentialRampToValueAtTime(1, now + offset + 0.012);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.32);
          osc.connect(gain);
          gain.connect(master);
          osc.start(now + offset);
          osc.stop(now + offset + 0.36);
        });
      } catch (error) {
        /* audio is a nicety, never a blocker */
      }
    }

    /** The label of the highest tier reached for this reward variant — a Shadow
        upgrade at $150 should say "3rd free Shadow Mind", not tier 1's label. */
    rewardLabelFor(state, variantId, progressValue) {
      let best = null;
      (state.rewards || []).forEach((reward) => {
        if (reward.id !== variantId) return;
        if (progressValue < reward.qty) return;
        if (!best || reward.qty > best.qty) best = reward;
      });
      return best ? best.label : null;
    }

    /* -- volume goal reward engine ---------------------------------------- */

    /**
     * Adds the reward pack once the paid quantity clears a tier and pulls it
     * back out when the cart drops below it. One mutation per pass, re-reading
     * server state each time, so it converges instead of racing itself.
     */
    async reconcileRewards(pass = 0, previousFingerprint = null) {
      if (pass >= MAX_REWARD_PASSES) return;
      const state = this.state;
      if (!state) return;
      // No ladder on this page (free packs off here): the list is empty and the
      // pass still runs — nothing is desired, so reward lines carried over from
      // a page that has the ladder are taken back out rather than left FREE.
      if (!Array.isArray(state.rewards)) state.rewards = [];

      // If a pass did not actually move the cart, stop. Without this a render
      // that failed to refresh this.state would re-add the same reward on every
      // pass and Shopify would merge them into one line of quantity 4.
      const fingerprint = JSON.stringify([
        state.paid_qty,
        (state.items || []).map((item) => [item.key, item.quantity, item.reward]),
      ]);
      if (fingerprint === previousFingerprint) return;

      // Thresholds arrive already normalised to the ladder's basis (pack count
      // or cart total in cents), so this compares like with like either way.
      const progressValue = state.progress_value != null ? state.progress_value : state.paid_qty || 0;
      // What the shelf currently holds, per reward variant. Tiers 1-3 all grant
      // Shadow Mind, so they must be reconciled as ONE quantity — otherwise
      // $150 shows three separate one-unit "FREE" lines.
      //
      // One variant can also sit on several lines: when the discount covers
      // only part of a quantity, Shopify SPLITS the line — a $0 line for the
      // covered units and a charged line for the rest, each with its own key.
      // So a variant is a set of keys, with free and charged units counted apart.
      const present = new Map();
      (state.items || []).forEach((item) => {
        if (!item.reward) return;
        const entry = present.get(item.id) || { keys: [], quantity: 0, free: 0, charged: 0 };
        entry.keys.push(item.key);
        entry.quantity += item.quantity;
        if (Number(item.paid) > 0) {
          entry.charged += item.quantity;
        } else {
          entry.free += item.quantity;
        }
        present.set(item.id, entry);
      });

      // Every reward correction goes out as ONE /cart/update. Shopify re-applies
      // the discounts after each mutation, so fixing lines one at a time let it
      // judge half-finished carts: pulling the Gold first left three Shadow
      // Minds against a $100 entitlement of two, Shopify split off a charged
      // unit, and the sweep below read that as "discount refused" and wiped
      // every reward. A split variant is one quantity over several keys — it
      // all goes on the first key and the rest are zeroed; Shopify re-splits
      // the total itself if it has to.
      const setTotals = async (totals) => {
        const updates = {};
        totals.forEach((want, variantId) => {
          const entry = present.get(variantId);
          if (!entry) return;
          entry.keys.forEach((key, index) => {
            updates[key] = index === 0 ? want : 0;
          });
        });
        try {
          await this.request(silentUpdateUrl(), { updates });
          return true;
        } catch (error) {
          await this.refresh().catch(() => {});
          return false;
        }
      };

      // What it should hold. Tiers normally STACK: each reached tier contributes
      // its grant, and tiers granting the same pack sum together, so $100
      // (grant 2) plus $150 (grant 1) is one line of three.
      const reached = state.rewards.filter(
        (reward) =>
          reward.id &&
          !this.blockedRewardVariants.has(reward.id) &&
          progressValue >= reward.qty
      );

      // ...except a tier marked `replaces`, which supersedes everything below
      // it. That exists because Shopify applies only ONE automatic product
      // discount per order: with three free Shadow Minds AND a free Gold in the
      // cart, the Shadow Mind rule wins the contest on its own terms and the
      // Gold is charged in full. Leaving only the Gold is what makes its
      // discount the one that applies. Measured, not assumed — a $203 cart
      // holding just the Gold settles at $0.00; the same cart holding the
      // Shadow Minds too settles the Gold at $24.99.
      const replacing = reached
        .filter((reward) => reward.replaces)
        .sort((a, b) => b.qty - a.qty)[0];
      const effective = replacing
        ? reached.filter((reward) => reward.qty >= replacing.qty)
        : reached;

      const desired = new Map();
      effective.forEach((reward) => {
        const grant = Math.max(1, parseInt(reward.grant, 10) || 1);
        desired.set(reward.id, (desired.get(reward.id) || 0) + grant);
      });

      // Never ask for more than Shopify just showed it will give free at this
      // spend, or the trim above and this add would fight until the pass cap.
      desired.forEach((want, variantId) => {
        const ceiling = this.rewardCeilings.get(variantId);
        if (ceiling && ceiling.at === progressValue && want > ceiling.max) {
          desired.set(variantId, ceiling.max);
        }
      });

      // Charged reward units. Shopify, not this script, decides whether a reward
      // is free: the packs carry a real price and a Buy-X-Get-Y discount zeroes
      // them once the spend qualifies. A charged unit means one of three things:
      //  - the spend fell below the tier (the shopper removed something):
      //    nothing is owed, so just pull it — blocking here is what used to
      //    stop the packs coming back when the shopper added more again;
      //  - the ladder says it is earned but NONE of it is free: the discount did
      //    not apply (a code discount displaced it). Pull it and stop offering
      //    it this session, or the drawer would charge for a pack it calls FREE;
      //  - SOME of it is free: Shopify split the line because the spend covers
      //    fewer packs than asked for. Trim back to the free units and remember
      //    that ceiling for this spend — the shopper earned those, so blocking
      //    the pack outright would take away rewards they qualify for.
      const trims = new Map();
      present.forEach((entry, variantId) => {
        if (!entry.charged) return;
        if (entry.free > 0) {
          trims.set(variantId, entry.free);
          this.rewardCeilings.set(variantId, { at: progressValue, max: entry.free });
        } else {
          trims.set(variantId, 0);
          if (desired.get(variantId)) this.blockedRewardVariants.add(variantId);
        }
      });
      if (trims.size) {
        await setTotals(trims);
        return this.reconcileRewards(pass + 1, fingerprint);
      }

      // 1. Correct or remove a reward line that no longer matches — every one
      //    of them in the same request.
      const corrections = new Map();
      let upgraded = null;
      present.forEach((entry, variantId) => {
        const want = desired.get(variantId) || 0;
        if (entry.quantity === want) return;
        corrections.set(variantId, want);
        if (want > entry.quantity) upgraded = variantId;
      });
      if (corrections.size) {
        const ok = await setTotals(corrections);
        // Tiers 1-3 all grant Shadow Mind, so crossing $100 and $150 only grows
        // this line. Without this the shopper hits two of the four $50 marks in
        // silence.
        if (ok && upgraded) {
          this.celebrate(this.rewardLabelFor(state, upgraded, progressValue));
        }
        return this.reconcileRewards(pass + 1, fingerprint);
      }

      // 2. Add a reward that is earned but not in the shelf at all.
      for (const [variantId, want] of desired) {
        if (present.has(variantId)) continue;
        const reward = state.rewards.find((r) => r.id === variantId) || {};
        const landedLabel = this.rewardLabelFor(state, variantId, progressValue);
        try {
          // skipRefresh: only the ADD decides whether the reward is available.
          // A redraw failing afterwards must not mark a pack that did land as
          // "unavailable" and block it for the session.
          await this.request(
            silentAddUrl(),
            {
              items: [
                { id: variantId, quantity: want, properties: { [REWARD_PROPERTY]: String(variantId) } },
              ],
            },
            { skipRefresh: true }
          );
          this.celebrate(landedLabel);
        } catch (error) {
          // Usually out of stock. Remember it for the whole session so this does
          // not fire a failing POST on every page view, and say so once.
          this.blockedRewardVariants.add(variantId);
          writeBlockedRewards(this.blockedRewardVariants);
          await this.refresh().catch(() => {});
          this.showError((reward.label || 'That reward') + ' is unavailable right now.');
          return this.reconcileRewards(pass + 1, fingerprint);
        }
        // The add response already redrew the shelf unless it carried no
        // section (the ?view= preview); then redraw now, failure tolerated.
        if (/[?&]view=/.test(window.location.search)) await this.refresh().catch(() => {});
        return this.reconcileRewards(pass + 1, fingerprint);
      }
    }
  }

  if (!customElements.get('se-cart-drawer')) {
    customElements.define('se-cart-drawer', SECartDrawer);
  }

  /* ------------------------------------------------- on-page quantity stepper */

  class SEBuyQuantity extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.input = this.querySelector('[data-se-qty-input]');
      this.max = parseInt(this.dataset.max, 10) || 0;
      this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
      this.form = this.closest('form');
      this.cta = this.form ? this.form.querySelector('[data-se-add-cta]') : null;

      // A card's CTA is ~114px wide — far too narrow to carry a price without
      // truncating it, and the price is already shown above the button. So the
      // running total is written into the price line instead, where there is
      // room. Any section can opt in with data-se-buy-context + these hooks.
      const context = this.closest('[data-se-buy-context]');
      this.lineTotal = context && context.querySelector('[data-se-line-total]');
      this.unitNote = context && context.querySelector('[data-se-unit-note]');
      this.lineTotalInitial = this.lineTotal ? this.lineTotal.innerHTML : null;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-se-qty-step]');
        if (!button) return;
        event.preventDefault();
        this.setQuantity(this.quantity + (parseInt(button.dataset.seQtyStep, 10) || 0));
      });

      this.addEventListener('change', () => this.setQuantity(this.quantity));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target === this.input) event.preventDefault();
      });

      // Quantity 1 keeps the label Liquid rendered — re-formatting it here would
      // print the shop currency over a Markets presentment price.
      this.render(true);
    }

    get quantity() {
      return parseInt(this.input && this.input.value, 10) || 1;
    }

    setQuantity(value) {
      let quantity = Number.isFinite(value) ? value : 1;
      if (quantity < 1) quantity = 1;
      if (this.max > 0 && quantity > this.max) quantity = this.max;
      if (this.input) this.input.value = quantity;
      this.render();
    }

    render(initial) {
      const quantity = this.quantity;

      const down = this.querySelector('[data-se-qty-step="-1"]');
      const up = this.querySelector('[data-se-qty-step="1"]');
      if (down) down.disabled = quantity <= 1;
      if (up) up.disabled = this.max > 0 && quantity >= this.max;

      // Quantity 1 keeps whatever Liquid rendered — re-formatting it here would
      // print the shop currency over a Markets presentment price.
      if (this.lineTotal && !(initial && quantity === 1)) {
        this.lineTotal.innerHTML =
          quantity > 1 ? SEMoney.format(this.unitPrice * quantity) : this.lineTotalInitial;
      }

      if (this.unitNote) {
        if (quantity > 1) {
          this.unitNote.textContent = `${quantity} × ${stripTags(SEMoney.format(this.unitPrice))}`;
          this.unitNote.hidden = false;
        } else {
          this.unitNote.hidden = true;
        }
      }

      if (!this.cta) return;
      if (initial && quantity === 1) return;

      const template = quantity > 1 ? this.cta.dataset.seCtaMulti : this.cta.dataset.seCtaSingle;
      if (!template) return;

      const label = template
        .replace('[qty]', String(quantity))
        .replace('[price]', SEMoney.format(this.unitPrice * quantity));

      const target = this.cta.querySelector('[data-se-cta-label]') || this.cta;
      target.innerHTML = label;
    }
  }

  if (!customElements.get('se-buy-quantity')) {
    customElements.define('se-buy-quantity', SEBuyQuantity);
  }

  /* ---------------------------------------------------------- pack builder */

  /**
   * The four packs are one order, not four. Every stepper starts at 0 and feeds
   * a single "Add N packs" button, so a shopper can pick 2 Shadow + 3 Sylvie and
   * commit them in one request instead of opening the shelf four times.
   */
  class SEPackBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.cta = this.querySelector('[data-se-builder-add]');
      this.label = this.querySelector('[data-se-builder-label]');
      this.countEl = this.querySelector('[data-se-builder-count]');
      this.totalEl = this.querySelector('[data-se-builder-total]');
      this.goalEl = this.querySelector('[data-se-builder-goal]');
      if (!this.cta) return;

      // The reward ladder, rendered as data by se-builder-milestones.liquid.
      // Absent or malformed simply means no goal line — the builder must never
      // fail to add packs because a merchant left the ladder half-configured.
      this.milestones = [];
      const blob = this.querySelector('[data-se-builder-milestones]');
      if (blob) {
        try {
          const parsed = JSON.parse(blob.textContent || '[]');
          if (Array.isArray(parsed)) {
            this.milestones = parsed
              .filter((m) => m && Number(m.v) > 0 && m.l)
              .sort((a, b) => Number(a.v) - Number(b.v));
          }
        } catch (error) {
          // Leave the ladder empty and carry on.
        }
      }

      this.copy = {
        empty: this.dataset.emptyLabel || 'Pick your packs',
        ctaEmpty: (this.label && this.label.textContent.trim()) || 'Add packs to cart',
        ctaOne: this.dataset.ctaOne || 'Add [qty] pack to cart',
        ctaMany: this.dataset.ctaMany || 'Add [qty] packs to cart',
        selected: this.dataset.selected || '[qty] selected',
        selectedOne: this.dataset.selectedOne || '[qty] pack selected',
        goalAmount: this.dataset.goalAmountText || 'Spend [amount] more → [reward]',
        goalCount: this.dataset.goalCountText || 'Add [count] more [unit] → [reward]',
        goalMet: this.dataset.goalMetText || '[reward] unlocked',
        unitOne: this.dataset.goalUnitOne || 'pack',
        unitMany: this.dataset.goalUnitMany || 'packs',
      };
      this.goalIsAmount = this.dataset.goalBasis === 'amount';

      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-se-qty-step]');
        if (step && this.contains(step)) {
          event.preventDefault();
          this.step(step);
          return;
        }
        if (event.target.closest('[data-se-builder-add]')) {
          event.preventDefault();
          this.commit();
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-se-qty-input]')) this.render();
      });

      // The cart can change without this builder being touched — the drawer's
      // own steppers, a quick-add, a reward landing. Re-render so the goal line
      // and the stock ceilings follow.
      this.onCartUpdate = () => this.render();
      document.addEventListener('se:cart:updated', this.onCartUpdate);

      this.render();
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener('se:cart:updated', this.onCartUpdate);
    }

    get rows() {
      return [...this.querySelectorAll('[data-se-builder-item]')];
    }

    /** Units of this variant already in the shelf — Shopify does not decrement
        inventory for cart contents, so the raw stock number is not the ceiling. */
    inShelf(variantId) {
      const drawer = getDrawer();
      const items = (drawer && drawer.state && drawer.state.items) || [];
      return items.reduce((sum, item) => (item.id === variantId ? sum + item.quantity : sum), 0);
    }

    readRow(row) {
      const input = row.querySelector('[data-se-qty-input]');
      const id = parseInt(row.dataset.seVariant, 10);
      const stock = parseInt(row.dataset.seMax, 10);

      let max = 0;
      if (Number.isFinite(stock) && stock > 0) {
        max = Math.max(0, stock - this.inShelf(id));
      }

      let qty = parseInt(input && input.value, 10);
      if (!Number.isFinite(qty) || qty < 0) qty = 0;
      if (max > 0 && qty > max) qty = max;
      if (input) {
        input.value = qty;
        if (max > 0) input.setAttribute('max', String(max));
      }

      return { input, qty, max, id, price: parseInt(row.dataset.sePrice, 10) || 0 };
    }

    step(button) {
      if (this.busy) return;
      const row = button.closest('[data-se-builder-item]');
      if (!row) return;
      const state = this.readRow(row);
      const delta = parseInt(button.dataset.seQtyStep, 10) || 0;
      let next = state.qty + delta;
      if (next < 0) next = 0;
      if (state.max && next > state.max) next = state.max;
      if (state.input) state.input.value = next;
      this.render();
    }

    render() {
      let qty = 0;
      let total = 0;

      this.rows.forEach((row) => {
        const state = this.readRow(row);
        qty += state.qty;
        total += state.qty * state.price;
        row.classList.toggle('is-picked', state.qty > 0);

        // aria-disabled, not disabled: disabling the button the user is
        // standing on drops focus to <body>. step() no-ops instead.
        const down = row.querySelector('[data-se-qty-step="-1"]');
        const up = row.querySelector('[data-se-qty-step="1"]');
        const setOff = (el, off) => {
          if (!el) return;
          el.setAttribute('aria-disabled', off ? 'true' : 'false');
          el.classList.toggle('is-off', off);
        };
        setOff(down, state.qty <= 0);
        setOff(up, state.max > 0 && state.qty >= state.max);
      });

      this.selection = qty;
      // `busy` matters: the stepper and input handlers also call render(), and
      // without it they would re-enable the button mid-request and allow a
      // second, duplicate add.
      this.cta.disabled = this.busy || qty === 0;

      if (this.label) {
        const template = qty === 0 ? this.copy.ctaEmpty : qty === 1 ? this.copy.ctaOne : this.copy.ctaMany;
        this.label.textContent = template.replace('[qty]', String(qty));
      }
      if (this.countEl) {
        const summary = qty === 0 ? this.copy.empty : qty === 1 ? this.copy.selectedOne : this.copy.selected;
        this.countEl.textContent = summary.replace('[qty]', String(qty));
      }
      if (this.totalEl) {
        this.totalEl.hidden = qty === 0;
        this.totalEl.innerHTML = qty === 0 ? '' : SEMoney.format(total);
      }

      // Drives the sticky commit bar on small screens: a live selection has to
      // stay reachable while the shopper keeps swiping the rail, and an empty
      // bar pinned over the page is just furniture.
      this.classList.toggle('is-committing', qty > 0);

      this.renderGoal(qty, total);
    }

    /**
     * What the shopper has ALREADY banked, in the ladder's own unit. The
     * thresholds are measured against cart spend, not against this picker, so
     * a shopper holding $60 who picks $20 here is at $80 and one rung away —
     * reading the picker alone would tell them they are at $20 and lie.
     *
     * `progress_value` is the drawer's own paid-only figure (reward lines
     * excluded), the same number reconcileRewards() grants against, so the
     * page and the engine can never disagree about which rung is next.
     */
    cartProgress() {
      const drawer = getDrawer();
      const state = drawer && drawer.state;
      if (!state) return 0;
      const value = state.progress_value != null ? state.progress_value : state.paid_qty;
      return Number(value) || 0;
    }

    /**
     * One rung, never the whole ladder — and never a claim that rungs combine.
     * Shopify applies a single automatic product discount per order, so naming
     * the next reward is the only promise this line can honestly make.
     */
    renderGoal(qty, total) {
      const node = this.goalEl;
      if (!node) return;

      if (!this.milestones.length) {
        node.hidden = true;
        return;
      }

      const progress = this.cartProgress() + (this.goalIsAmount ? total : qty);
      const next = this.milestones.find((m) => progress < Number(m.v));

      // Past the top rung: the ladder has nothing left to say, and inventing a
      // congratulation the drawer will repeat is just noise on the page.
      const justMet = [...this.milestones].reverse().find((m) => progress >= Number(m.v));
      if (!next) {
        if (!justMet || qty === 0) {
          node.hidden = true;
          return;
        }
        node.hidden = false;
        node.classList.add('is-met');
        node.innerHTML = this.goalLine(
          'celebration',
          this.fill(this.copy.goalMet, { reward: this.rewardName(justMet.l) }),
          justMet
        );
        return;
      }

      // Nothing picked and nothing banked — the bar is at rest, so stay quiet
      // rather than opening with a demand to spend.
      if (qty === 0 && progress === 0) {
        node.hidden = true;
        return;
      }

      const remaining = Number(next.v) - progress;
      const message = this.goalIsAmount
        ? this.fill(this.copy.goalAmount, {
            amount: markup(`<strong>${SEMoney.format(remaining)}</strong>`),
            reward: this.rewardName(next.l),
          })
        : this.fill(this.copy.goalCount, {
            count: markup(`<strong>${remaining}</strong>`),
            unit: remaining === 1 ? this.copy.unitOne : this.copy.unitMany,
            reward: this.rewardName(next.l),
          });

      node.hidden = false;
      node.classList.remove('is-met');
      node.innerHTML = this.goalLine(next.k === 'ship' ? 'local_shipping' : 'redeem', message, next);
    }

    /**
     * Icon + message + the reward's own picture, as flex children.
     *
     * The message must be wrapped: left as bare text nodes beside the <strong>
     * amount, flex treats each run as its own item and the sentence breaks
     * mid-phrase on a narrow screen — "Spend $16.11 / more → 3rd free Shadow /
     * Mind".
     *
     * The thumbnail is decorative: the reward is already named in the sentence
     * beside it, so alt="" keeps a screen reader from hearing it twice.
     */
    /**
     * The reward is the point of the sentence, so it gets its own line and the
     * weight to match. Block display is what forces the break: as inline text
     * the name wrapped wherever it happened to run out of room — "2 free /
     * Shadow Minds" — which reads as a mistake rather than as emphasis.
     *
     * Escaped here because the result is passed through as markup.
     */
    rewardName(label) {
      return markup(`<strong class="se-builder__goal-reward">${escapeHtml(label)}</strong>`);
    }

    goalLine(icon, message, milestone) {
      const thumb =
        milestone && milestone.img
          ? `<img class="se-builder__goal-thumb" src="${escapeHtml(milestone.img)}" alt="" ` +
            `width="34" height="34" loading="lazy" decoding="async">`
          : '';
      return (
        `<span class="se-builder__goal-icon material-symbols-outlined" aria-hidden="true">${icon}</span>` +
        `<span class="se-builder__goal-text">${message}</span>` +
        thumb
      );
    }

    /**
     * Fill a merchant template that is about to be written as innerHTML.
     *
     * Liquid escaped the template into the data attribute, but `dataset` hands
     * it back DECODED — so a merchant who typed markup into the theme editor
     * would have it injected live. The template is therefore re-escaped here,
     * and so is every value except the ones we wrapped in markup() ourselves.
     * Placeholders survive escaping because `[amount]` has no special chars.
     */
    fill(template, values) {
      return Object.keys(values).reduce((out, key) => {
        const raw = values[key];
        const safe = raw && raw.html != null ? raw.html : escapeHtml(raw);
        return out.split(`[${key}]`).join(safe);
      }, escapeHtml(template));
    }

    commit() {
      if (this.busy) return;
      const drawer = getDrawer();
      if (!drawer) return;

      const items = this.rows
        .map((row) => {
          const state = this.readRow(row);
          return { id: state.id, quantity: state.qty };
        })
        .filter((i) => i.quantity > 0);

      if (!items.length) return;

      this.busy = true;
      this.cta.disabled = true;
      this.cta.setAttribute('aria-busy', 'true');

      drawer
        .addItems(items, this.cta)
        .then((ok) => {
          // Only clear the selection if it actually reached the cart. The add
          // is atomic, so on failure the shopper still has nothing — wiping the
          // builder too would destroy the whole pick for nothing.
          if (!ok) return;
          this.rows.forEach((row) => {
            const input = row.querySelector('[data-se-qty-input]');
            if (input) input.value = 0;
          });
          // The opener is about to be disabled again, and focusing a disabled
          // button drops to <body> when the shelf closes.
          drawer.setActiveElement(this.querySelector('[data-se-qty-step="1"]'));
        })
        .finally(() => {
          this.busy = false;
          this.cta.removeAttribute('aria-busy');
          this.render();
        });
    }
  }

  if (!customElements.get('se-pack-builder')) {
    customElements.define('se-pack-builder', SEPackBuilder);
  }

  /* ----------------------------------------------------------- packs slider */

  /** Arrow paging + arrow state for the packs rail. Scroll-snap does the rest. */
  class SEPackRail extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      const scope = this.closest('.se-packs');
      this.nav = scope && scope.querySelector('[data-se-rail-nav]');
      if (!this.nav) return;

      this.prev = this.nav.querySelector('[data-se-rail-prev]');
      this.next = this.nav.querySelector('[data-se-rail-next]');
      this.ticking = false;

      this.prev.addEventListener('click', () => this.page(-1));
      this.next.addEventListener('click', () => this.page(1));
      this.addEventListener('scroll', () => this.queueSync(), { passive: true });
      window.addEventListener('resize', () => this.queueSync());

      this.sync();
    }

    get step() {
      const card = this.querySelector('.se-pack');
      if (!card) return this.clientWidth;
      const gap = parseFloat(getComputedStyle(this).columnGap) || 20;
      return card.getBoundingClientRect().width + gap;
    }

    page(direction) {
      const step = this.step;
      const perView = Math.max(1, Math.floor(this.clientWidth / step));
      this.scrollBy({ left: direction * step * perView, behavior: 'smooth' });
    }

    queueSync() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.sync();
      });
    }

    sync() {
      // Ignore sub-pixel/rounding overflow — arrows for 20px of travel are noise.
      const overflowing = this.scrollWidth - this.clientWidth > 24;
      this.nav.hidden = !overflowing;
      if (!overflowing) return;
      this.prev.disabled = this.scrollLeft <= 2;
      this.next.disabled = this.scrollLeft >= this.scrollWidth - this.clientWidth - 2;
    }
  }

  if (!customElements.get('se-pack-rail')) {
    customElements.define('se-pack-rail', SEPackRail);
  }

  /* ----------------------------------------------------- probability board ---- */

  /**
   * The four pack sections were each hand-built and spell the same widget
   * differently: shadow uses `.probability-toggle-btn` / `#probability-board-
   * wrapper`, gold prefixes both with `gold-`, platinum with `platinum-`.
   * "See every card" matched only shadow's spelling, so on gold and platinum
   * it scrolled the shopper to a heading and a chevron and opened nothing.
   *
   * Match on the shared suffix instead of the exact class, so a fourth spelling
   * cannot silently break it again.
   */
  const ODDS_TOGGLE_SELECTOR = '[class*="probability-toggle-btn"]';
  const ODDS_BOARD_SELECTOR = '[id$="probability-board-wrapper"]';

  /**
   * Give the board a label people can read.
   *
   * The control was a bare circular chevron under a title — no words, no
   * aria-expanded, nothing to say it opens anything. Scrolling past, it reads
   * as decoration or a "keep scrolling" arrow, which is why the odds went
   * unseen. Published rates are this store's most citable asset; they should
   * not be behind an unlabelled button.
   *
   * Done here rather than in the four sections because they have diverged
   * enough that one normaliser is more maintainable than four parallel edits —
   * and a shopper with no JS still gets the board, just always expanded.
   */
  const initOddsBoards = () => {
    document.querySelectorAll(ODDS_TOGGLE_SELECTOR).forEach((toggle, index) => {
      if (toggle.dataset.seOddsReady) return;
      toggle.dataset.seOddsReady = '1';

      const scope = toggle.closest('.shopify-section') || document;
      const board = scope.querySelector(ODDS_BOARD_SELECTOR);
      if (!board) return;

      if (!board.id) board.id = `se-odds-board-${index}`;
      toggle.setAttribute('aria-controls', board.id);

      const label = document.createElement('span');
      label.className = 'se-odds-toggle__label';
      toggle.appendChild(label);

      const sync = () => {
        const open = board.classList.contains('is-visible');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        label.textContent = open ? 'Hide the odds' : 'See the odds';
        // The section's own handler already owns the class; this only reports.
        toggle.classList.toggle('se-odds-toggle--open', open);
      };

      sync();
      // The section's inline script flips `is-visible` on its own, and there is
      // no event for a class change — observe it rather than re-implement the
      // toggle and risk the two fighting.
      new MutationObserver(sync).observe(board, { attributes: true, attributeFilter: ['class'] });
    });
  };

  /* ------------------------------------------------------- floating cart button */

  /**
   * Reveal the floating cart once the page has scrolled past the hero header,
   * which carries its own cart icon in the same corner. Showing both at once
   * would stack two cart buttons a few pixels apart.
   *
   * Threshold is a plain scroll offset rather than an IntersectionObserver on
   * the header: the header is `position: absolute` inside a section that is
   * re-rendered by the theme editor, so a node reference goes stale on every
   * section reload while a scroll offset never does.
   */
  const initCartFab = () => {
    const fab = document.querySelector('[data-se-cart-fab]');
    if (!fab) return;

    const REVEAL_AT = 140;
    // A full cart earns a pulse. Below this the badge alone is enough — a ring
    // breathing over a one-item cart is noise, not a nudge.
    const PULSE_FROM = 6;
    let shown = null;
    let ticking = false;

    const syncFullness = (state) => {
      const count = state ? Number(state.item_count) || 0 : 0;
      fab.classList.toggle('se-cart-fab--full', count >= PULSE_FROM);
    };

    // The drawer announces every cart change; the direct read covers the load
    // order where the element upgraded before this ran.
    document.addEventListener('se:cart:updated', (event) => {
      syncFullness(event.detail && event.detail.state);
    });
    const drawer = getDrawer();
    syncFullness(drawer && drawer.state);

    const apply = () => {
      ticking = false;
      const next = window.scrollY > REVEAL_AT;
      if (next === shown) return;
      shown = next;
      fab.classList.toggle('se-cart-fab--in', next);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    apply();
  };

  const initEnhancements = () => {
    initCartFab();
    initOddsBoards();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEnhancements);
  } else {
    initEnhancements();
  }

  // Theme-editor section reloads replace the markup wholesale.
  document.addEventListener('shopify:section:load', initOddsBoards);

  /* ------------------------------------------------------------ global wiring */

  // Every /cart link opens the drawer instead. The route itself stays alive as
  // the no-JS fallback and for /cart/:variant:qty permalinks.
  document.addEventListener('click', (event) => {
    // Leave new-tab / new-window / download / already-handled clicks alone.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('data-se-allow-cart') || link.target === '_blank') return;

    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin) return;

    const cartPath = (cartRoutes().cart_url || '/cart').replace(/\/$/, '');
    const path = url.pathname.replace(/\/$/, '');
    if (path !== cartPath) return;

    const drawer = getDrawer();
    if (!drawer) return;

    event.preventDefault();
    drawer.open(link);
  });

  // Any product form that Dawn's <product-form> is not already handling.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute('data-se-skip') || form.closest('product-form')) return;

      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add\b/.test(action)) return;

      const drawer = getDrawer();
      if (!drawer) return;

      event.preventDefault();
      drawer.addFromForm(form);
    },
    true
  );

  // "See every card" — jump to that pack's probability board, opening it first
  // if the section keeps it collapsed (its own toggle handles the scroll then,
  // so the two do not fight over scroll position).
  document.addEventListener('click', (event) => {
    const jump = event.target.closest('[data-se-odds-jump]');
    if (!jump) return;

    const target = document.getElementById(jump.dataset.seOddsJump);
    if (!target) return;

    event.preventDefault();

    const scope = target.closest('.shopify-section') || document;
    const toggle = scope.querySelector(ODDS_TOGGLE_SELECTOR);
    const board = scope.querySelector(ODDS_BOARD_SELECTOR);

    const scrollToAnchor = () => {
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    if (toggle && board && !board.classList.contains('is-visible')) {
      toggle.click();
      // Expanding changes the page height, and the section's own handler scrolls
      // immediately — so wait for the reveal to settle, then land on the heading.
      let done = false;
      const settle = () => {
        if (done) return;
        done = true;
        board.removeEventListener('transitionend', settle);
        scrollToAnchor();
      };
      board.addEventListener('transitionend', settle, { once: true });
      setTimeout(settle, 700);
      return;
    }

    scrollToAnchor();
  });

  // Quick-add inside the shelf — never closes it.
  document.addEventListener('click', (event) => {
    const quick = event.target.closest('[data-se-quick-add]');
    if (!quick) return;
    const drawer = getDrawer();
    if (!drawer) return;

    event.preventDefault();
    if (quick.getAttribute('aria-disabled') === 'true' || quick.disabled) return;
    const id = parseInt(quick.dataset.seQuickAdd, 10);
    if (!id) return;

    // Disabling blurs the button, so applySection would see <body> and fall back
    // to the close button — leaving the next Enter to shut the shelf. Tell the
    // drawer which tile to come back to, keyed on variant, not position.
    drawer.pendingFocus = '[data-se-quick-add="' + id + '"]';

    const list = quick.closest('[data-se-more-list]');
    const scrollLeft = list ? list.scrollLeft : 0;

    quick.setAttribute('aria-disabled', 'true');
    quick.setAttribute('aria-busy', 'true');

    drawer.addItems([{ id, quantity: 1 }], quick, true).finally(() => {
      const nextList = document.querySelector('[data-se-more-list]');
      if (nextList) nextList.scrollLeft = scrollLeft;
      if (quick.isConnected) {
        quick.removeAttribute('aria-disabled');
        quick.removeAttribute('aria-busy');
      }
    });
  });

  // Explicit triggers, e.g. a "View cart" button.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-se-cart-open]');
    if (!trigger) return;
    const drawer = getDrawer();
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });
})();
